/* eslint-disable no-console */
/* eslint-disable no-unsafe-optional-chaining */
import {
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
  HStack,
  Text,
  Heading,
  Stack,
  VStack,
  Icon,
  Badge,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Select,
  Spinner,
  Center,
  Tooltip,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButtons from '../../Components/Buttons/BackButtons';
import useUserStore from '../../Hooks/Zustand/Store';
import DatePicker from '../../Components/DatePicker/DatePicker';
import { 
  FaRegCalendar, 
  FaTrophy, 
  FaTasks, 
  FaClock, 
  FaChartLine,
  FaProjectDiagram,
  FaCheckCircle
} from 'react-icons/fa';
import { TbReload } from 'react-icons/tb';
import { useTranslation } from 'react-i18next';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  getAggregateFromServer,
  getCountFromServer,
  sum,
  average,
  count,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../Config/firebase';
import { msToTime } from '../../Utils/timeUtil';

function ManagementDashboard() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedProject, setSelectedProject] = useState('');
  
  const [statistics, setStatistics] = useState({
    totalBoards: 0,
    totalTasks: 0,
    totalScore: 0,
    avgScore: 0,
    avgTime: 0,
    topBoards: [],
    recentActivity: [],
    projectStats: [],
  });

  const [moduleUsage, setModuleUsage] = useState({
    kanban: 0,
    mom: 0,
    flowchart: 0,
    mindmap: 0,
    assets: 0,
    operations: 0,
  });

  const dates = useRef({
    startDate: moment().subtract(30, 'days').unix(),
    endDate: moment().unix(),
  });

  const globalState = useUserStore();
  const navigate = useNavigate();

  // HELPER FUNCTIONS FOR SAFE NUMBER FORMATTING
  const safeNumber = (value, defaultValue = 0) => {
    const num = Number(value);
    return isNaN(num) || !isFinite(num) ? defaultValue : num;
  };

  const formatNumber = (value, decimals = 0) => {
    const num = safeNumber(value);
    return num.toFixed(decimals);
  };

  const safeUnix = (timestamp) => {
    if (!timestamp) return moment().unix();
    if (typeof timestamp === 'number') return timestamp;
    if (timestamp?.seconds) return timestamp.seconds;
    return moment().unix();
  };

  const getModuleUsageCount = async () => {
    if (!globalState.currentCompany) return;

    try {
      const filesRef = collection(db, 'files');
      const flowchartsRef = collection(db, 'flowcharts');

      // Count Kanban boards
      const kanbanQuery = query(
        filesRef,
        where('companyId', '==', globalState.currentCompany),
        where('type', '==', 'kanban')
      );
      const kanbanSnapshot = await getCountFromServer(kanbanQuery);
      const kanbanCount = safeNumber(kanbanSnapshot.data()?.count, 0);

      // Count MoM
      const momQuery = query(
        filesRef,
        where('companyId', '==', globalState.currentCompany),
        where('type', '==', 'mom')
      );
      const momSnapshot = await getCountFromServer(momQuery);
      const momCount = safeNumber(momSnapshot.data()?.count, 0);

      // Count Digital Assets (folders)
      const assetsQuery = query(
        collection(db, 'folders'),
        where('companyId', '==', globalState.currentCompany),
        where('type', '==', 'digital_assets')
      );
      const assetsSnapshot = await getCountFromServer(assetsQuery);
      const assetsCount = safeNumber(assetsSnapshot.data()?.count, 0);

      // Count Operations
      const operationsQuery = query(
        filesRef,
        where('companyId', '==', globalState.currentCompany),
        where('type', '==', 'operation')
      );
      const operationsSnapshot = await getCountFromServer(operationsQuery);
      const operationsCount = safeNumber(operationsSnapshot.data()?.count, 0);

      // Count Flowcharts
      const flowchartQuery = query(
        flowchartsRef,
        where('companyId', '==', globalState.currentCompany),
        where('type', '==', 'flowchart')
      );
      const flowchartSnapshot = await getCountFromServer(flowchartQuery);
      const flowchartCount = safeNumber(flowchartSnapshot.data()?.count, 0);

      // Count Mindmaps
      const mindmapQuery = query(
        flowchartsRef,
        where('companyId', '==', globalState.currentCompany),
        where('type', '==', 'mindmap')
      );
      const mindmapSnapshot = await getCountFromServer(mindmapQuery);
      const mindmapCount = safeNumber(mindmapSnapshot.data()?.count, 0);

      setModuleUsage({
        kanban: kanbanCount,
        mom: momCount,
        flowchart: flowchartCount,
        mindmap: mindmapCount,
        assets: assetsCount,
        operations: operationsCount,
      });

    } catch (error) {
      console.error('Error counting module usage:', error);
    }
  };

  const getDashboardData = async () => {
    if (!globalState.currentCompany) return;

    setLoading(true);
    try {
      const { startDate, endDate } = dates.current;
      
      const startTimestamp = Timestamp.fromMillis(safeNumber(startDate, moment().subtract(30, 'days').unix()) * 1000);
      const endTimestamp = Timestamp.fromMillis(safeNumber(endDate, moment().unix()) * 1000);

      // Get all kanban boards
      const filesRef = collection(db, 'files');
      let filesQuery = query(
        filesRef,
        where('companyId', '==', globalState.currentCompany),
        where('type', '==', 'kanban')
      );

      if (selectedProject) {
        filesQuery = query(filesQuery, where('projectId', '==', selectedProject));
      }

      const filesSnapshot = await getDocs(filesQuery);
      
      if (filesSnapshot.empty) {
        setStatistics({
          totalBoards: 0,
          totalTasks: 0,
          totalScore: 0,
          avgScore: 0,
          avgTime: 0,
          topBoards: [],
          recentActivity: [],
          projectStats: [],
        });
        return;
      }

      // Aggregate data from all boards
      const boardPromises = filesSnapshot.docs.map(async (fileDoc) => {
        const fileData = fileDoc.data();
        const filesId = fileDoc.id;

        const kanbanRef = collection(db, 'kanban');
        const kanbanQuery = query(
          kanbanRef,
          where('filesId', '==', filesId),
          where('companyId', '==', globalState.currentCompany),
          where('column', '==', 'done'),
          where('lastUpdated', '>=', startTimestamp),
          where('lastUpdated', '<=', endTimestamp)
        );

        try {
          const aggregateSnapshot = await getAggregateFromServer(kanbanQuery, {
            totalScore: sum('score'),
            avgScore: average('score'),
            taskCount: count()
          });

          const aggregateData = aggregateSnapshot.data();
          const tasksSnapshot = await getDocs(kanbanQuery);
          
          let totalTime = 0;
          let taskWithTime = 0;

          tasksSnapshot.forEach((doc) => {
            const task = doc.data();
            if (task.time_completed_at?.seconds && task.time_backlog_at?.seconds) {
              const completedSeconds = safeNumber(task.time_completed_at.seconds, 0);
              const backlogSeconds = safeNumber(task.time_backlog_at.seconds, 0);
              const timeSpent = (completedSeconds - backlogSeconds) * 1000;
              
              if (timeSpent > 0) {
                totalTime += timeSpent;
                taskWithTime++;
              }
            }
          });

          const avgTime = taskWithTime > 0 ? totalTime / taskWithTime : 0;
          const project = globalState.projects?.find((p) => p.id === fileData.projectId);

          return {
            id: filesId,
            title: fileData.title || 'Untitled',
            totalScore: safeNumber(aggregateData.totalScore, 0),
            avgScore: safeNumber(aggregateData.avgScore, 0),
            tasks: tasksSnapshot.size,
            avgTime: safeNumber(avgTime, 0),
            projectId: fileData.projectId,
            projectName: project?.name || 'Unknown',
            lastUpdated: safeUnix(fileData.lastUpdated),
          };
        } catch (error) {
          console.error(`Error processing board ${filesId}:`, error);
          return null;
        }
      });

      let results = await Promise.all(boardPromises);
      results = results.filter((item) => item !== null && item.tasks > 0);

      // Calculate statistics with safe numbers
      const totalTasks = results.reduce((sum, item) => sum + safeNumber(item.tasks, 0), 0);
      const totalScore = results.reduce((sum, item) => sum + safeNumber(item.totalScore, 0), 0);
      const totalTime = results.reduce((sum, item) => sum + (safeNumber(item.avgTime, 0) * safeNumber(item.tasks, 0)), 0);
      const avgScore = totalTasks > 0 ? totalScore / totalTasks : 0;
      const avgTime = totalTasks > 0 ? totalTime / totalTasks : 0;

      // Top 5 boards by total score
      const topBoards = [...results]
        .sort((a, b) => safeNumber(b.totalScore, 0) - safeNumber(a.totalScore, 0))
        .slice(0, 5);

      // Recent activity (last 5 updated boards)
      const recentActivity = [...results]
        .sort((a, b) => safeNumber(b.lastUpdated, 0) - safeNumber(a.lastUpdated, 0))
        .slice(0, 5);

      // Project statistics
      const projectMap = {};
      results.forEach((board) => {
        if (!projectMap[board.projectId]) {
          projectMap[board.projectId] = {
            projectId: board.projectId,
            projectName: board.projectName,
            boards: 0,
            tasks: 0,
            totalScore: 0,
          };
        }
        projectMap[board.projectId].boards += 1;
        projectMap[board.projectId].tasks += safeNumber(board.tasks, 0);
        projectMap[board.projectId].totalScore += safeNumber(board.totalScore, 0);
      });

      const projectStats = Object.values(projectMap)
        .sort((a, b) => safeNumber(b.totalScore, 0) - safeNumber(a.totalScore, 0))
        .slice(0, 5);

      setStatistics({
        totalBoards: results.length,
        totalTasks: totalTasks,
        totalScore: totalScore,
        avgScore: avgScore,
        avgTime: avgTime,
        topBoards: topBoards,
        recentActivity: recentActivity,
        projectStats: projectStats,
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const onDateChange = (data) => {
    dates.current = {
      startDate: moment(data.startDate).unix(),
      endDate: moment(data.endDate).unix(),
    };
  };

  const handleReloadFilter = () => {
    dates.current = {
      startDate: moment().subtract(30, 'days').unix(),
      endDate: moment().unix(),
    };
    setSelectedProject('');
    getDashboardData();
  };

  useEffect(() => {
    if (globalState.currentCompany) {
      getDashboardData();
      getModuleUsageCount();
    }
  }, [globalState.currentCompany, selectedProject]);

  const getScoreColor = (score) => {
    const numScore = safeNumber(score, 0);
    if (numScore >= 8) return 'green';
    if (numScore >= 7) return 'blue';
    if (numScore >= 6) return 'yellow';
    return 'orange';
  };

  return (
    <Stack p={[2, 3, 5]} spacing={5}>
      {/* Header */}
      <HStack flexWrap="wrap" spacing={3}>
        <BackButtons />
        <VStack align="start" spacing={0}>
          <Heading size="lg" bgGradient="linear(to-r, blue.400, purple.500)" bgClip="text">
            📊 Management Dashboard
          </Heading>
          <Text fontSize="sm" color="gray.500">Overview & Performance Analytics</Text>
        </VStack>
      </HStack>

      {/* Filters */}
      <Card shadow="md">
        <CardBody>
          <HStack spacing={3} flexWrap="wrap">
            <Select
              placeholder="🎯 All Projects"
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              size="md"
              maxW="300px"
            >
              {globalState.projects?.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </Select>

            <Button
              onClick={onOpen}
              leftIcon={<FaRegCalendar />}
              colorScheme="blue"
              variant="outline"
              size="md"
            >
              {t('filterDate')}
            </Button>

            <Button
              onClick={handleReloadFilter}
              leftIcon={<TbReload />}
              colorScheme="purple"
              isDisabled={loading}
              size="md"
            >
              Reset
            </Button>

            <Text fontSize="sm" color="gray.500" ml="auto">
              {moment.unix(safeNumber(dates.current.startDate)).format('MMM DD')} - {moment.unix(safeNumber(dates.current.endDate)).format('MMM DD, YYYY')}
            </Text>
          </HStack>
        </CardBody>
      </Card>

      {loading ? (
        <Center py={20}>
          <VStack>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text color="gray.500">Loading dashboard...</Text>
          </VStack>
        </Center>
      ) : (
        <>
          {/* Key Metrics */}
          <SimpleGrid columns={[1, 2, 4]} spacing={4}>
            <Card bgGradient="linear(to-br, blue.400, blue.600)" color="white" shadow="lg">
              <CardBody>
                <HStack justify="space-between">
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" opacity={0.9}>Total Boards</Text>
                    <Heading size="xl">{statistics.totalBoards}</Heading>
                  </VStack>
                  <Icon as={FaProjectDiagram} boxSize={10} opacity={0.3} />
                </HStack>
              </CardBody>
            </Card>

            <Card bgGradient="linear(to-br, green.400, green.600)" color="white" shadow="lg">
              <CardBody>
                <HStack justify="space-between">
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" opacity={0.9}>Completed Tasks</Text>
                    <Heading size="xl">{statistics.totalTasks}</Heading>
                  </VStack>
                  <Icon as={FaCheckCircle} boxSize={10} opacity={0.3} />
                </HStack>
              </CardBody>
            </Card>

            <Card bgGradient="linear(to-br, yellow.400, yellow.600)" color="white" shadow="lg">
              <CardBody>
                <HStack justify="space-between">
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" opacity={0.9}>Total Score</Text>
                    <Heading size="xl">{formatNumber(statistics.totalScore, 0)}</Heading>
                    <Text fontSize="xs" opacity={0.8}>Avg: {formatNumber(statistics.avgScore, 2)}</Text>
                  </VStack>
                  <Icon as={FaTrophy} boxSize={10} opacity={0.3} />
                </HStack>
              </CardBody>
            </Card>

            <Card bgGradient="linear(to-br, purple.400, purple.600)" color="white" shadow="lg">
              <CardBody>
                <HStack justify="space-between">
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" opacity={0.9}>Avg Completion</Text>
                    <Heading size="xl">{msToTime(statistics.avgTime)}</Heading>
                  </VStack>
                  <Icon as={FaClock} boxSize={10} opacity={0.3} />
                </HStack>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Module Usage Statistics */}
          <Card shadow="lg" borderWidth={2} borderColor="blue.200">
            <CardHeader>
              <HStack>
                <Icon as={FaChartLine} color="blue.500" />
                <Heading size="md">Module Usage</Heading>
              </HStack>
            </CardHeader>
            <CardBody pt={0}>
              <SimpleGrid columns={[2, 3, 6]} spacing={3}>
                <Tooltip label="Kanban Boards">
                  <Card 
                    bg="blue.50" 
                    cursor="pointer"
                    _hover={{ bg: 'blue.100', transform: 'scale(1.05)' }}
                    transition="all 0.2s"
                    onClick={() => navigate('/kanban')}
                  >
                    <CardBody textAlign="center">
                      <VStack spacing={1}>
                        <Text fontSize="3xl">📋</Text>
                        <Heading size="lg" color="blue.600">{moduleUsage.kanban}</Heading>
                        <Text fontSize="xs" color="gray.600">Kanban</Text>
                      </VStack>
                    </CardBody>
                  </Card>
                </Tooltip>

                <Tooltip label="Minutes of Meeting">
                  <Card 
                    bg="green.50"
                    cursor="pointer"
                    _hover={{ bg: 'green.100', transform: 'scale(1.05)' }}
                    transition="all 0.2s"
                    onClick={() => navigate('/mom')}
                  >
                    <CardBody textAlign="center">
                      <VStack spacing={1}>
                        <Text fontSize="3xl">📝</Text>
                        <Heading size="lg" color="green.600">{moduleUsage.mom}</Heading>
                        <Text fontSize="xs" color="gray.600">MoM</Text>
                      </VStack>
                    </CardBody>
                  </Card>
                </Tooltip>

                <Tooltip label="Flowcharts">
                  <Card 
                    bg="purple.50"
                    cursor="pointer"
                    _hover={{ bg: 'purple.100', transform: 'scale(1.05)' }}
                    transition="all 0.2s"
                    onClick={() => navigate('/flowchart')}
                  >
                    <CardBody textAlign="center">
                      <VStack spacing={1}>
                        <Text fontSize="3xl">🔄</Text>
                        <Heading size="lg" color="purple.600">{moduleUsage.flowchart}</Heading>
                        <Text fontSize="xs" color="gray.600">Flowchart</Text>
                      </VStack>
                    </CardBody>
                  </Card>
                </Tooltip>

                <Tooltip label="Mindmaps">
                  <Card 
                    bg="pink.50"
                    cursor="pointer"
                    _hover={{ bg: 'pink.100', transform: 'scale(1.05)' }}
                    transition="all 0.2s"
                    onClick={() => navigate('/mindmap')}
                  >
                    <CardBody textAlign="center">
                      <VStack spacing={1}>
                        <Text fontSize="3xl">🧠</Text>
                        <Heading size="lg" color="pink.600">{moduleUsage.mindmap}</Heading>
                        <Text fontSize="xs" color="gray.600">Mindmap</Text>
                      </VStack>
                    </CardBody>
                  </Card>
                </Tooltip>

                <Tooltip label="Digital Assets">
                  <Card 
                    bg="orange.50"
                    cursor="pointer"
                    _hover={{ bg: 'orange.100', transform: 'scale(1.05)' }}
                    transition="all 0.2s"
                    onClick={() => navigate('/digital-assets')}
                  >
                    <CardBody textAlign="center">
                      <VStack spacing={1}>
                        <Text fontSize="3xl">🖼️</Text>
                        <Heading size="lg" color="orange.600">{moduleUsage.assets}</Heading>
                        <Text fontSize="xs" color="gray.600">Assets</Text>
                      </VStack>
                    </CardBody>
                  </Card>
                </Tooltip>

                <Tooltip label="Operations">
                  <Card 
                    bg="teal.50"
                    cursor="pointer"
                    _hover={{ bg: 'teal.100', transform: 'scale(1.05)' }}
                    transition="all 0.2s"
                    onClick={() => navigate('/operation')}
                  >
                    <CardBody textAlign="center">
                      <VStack spacing={1}>
                        <Text fontSize="3xl">⚙️</Text>
                        <Heading size="lg" color="teal.600">{moduleUsage.operations}</Heading>
                        <Text fontSize="xs" color="gray.600">Operations</Text>
                      </VStack>
                    </CardBody>
                  </Card>
                </Tooltip>
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* Top Boards & Recent Activity */}
          <SimpleGrid columns={[1, 1, 2]} spacing={4}>
            {/* Top Boards */}
            <Card shadow="lg">
              <CardHeader pb={2}>
                <HStack>
                  <Icon as={FaTrophy} color="yellow.500" />
                  <Heading size="md">Top 5 Boards</Heading>
                </HStack>
              </CardHeader>
              <CardBody pt={0}>
                {statistics.topBoards.length > 0 ? (
                  <Stack spacing={3}>
                    {statistics.topBoards.map((board, index) => (
                      <HStack
                        key={board.id}
                        p={3}
                        bg="gray.50"
                        borderRadius="md"
                        cursor="pointer"
                        _hover={{ bg: 'gray.100' }}
                        onClick={() => navigate(`/management/score/tasks/${board.id}`)}
                      >
                        <Badge colorScheme={index === 0 ? 'yellow' : 'gray'} fontSize="lg" px={2}>
                          #{index + 1}
                        </Badge>
                        <VStack align="start" spacing={0} flex={1}>
                          <Text fontWeight="bold" fontSize="sm" noOfLines={1}>
                            {board.title}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {board.projectName}
                          </Text>
                        </VStack>
                        <VStack align="end" spacing={0}>
                          <Badge colorScheme="yellow" fontSize="md">
                            {formatNumber(board.totalScore, 0)} pts
                          </Badge>
                          <Text fontSize="xs" color="gray.500">
                            {board.tasks} tasks
                          </Text>
                        </VStack>
                      </HStack>
                    ))}
                  </Stack>
                ) : (
                  <Text color="gray.500" fontSize="sm">No data available</Text>
                )}
              </CardBody>
            </Card>

            {/* Recent Activity */}
            <Card shadow="lg">
              <CardHeader pb={2}>
                <HStack>
                  <Icon as={FaChartLine} color="green.500" />
                  <Heading size="md">Recent Activity</Heading>
                </HStack>
              </CardHeader>
              <CardBody pt={0}>
                {statistics.recentActivity.length > 0 ? (
                  <Stack spacing={3}>
                    {statistics.recentActivity.map((board) => (
                      <HStack
                        key={board.id}
                        p={3}
                        bg="gray.50"
                        borderRadius="md"
                        cursor="pointer"
                        _hover={{ bg: 'gray.100' }}
                        onClick={() => navigate(`/management/score/tasks/${board.id}`)}
                      >
                        <VStack align="start" spacing={0} flex={1}>
                          <Text fontWeight="bold" fontSize="sm" noOfLines={1}>
                            {board.title}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {moment.unix(safeNumber(board.lastUpdated)).fromNow()}
                          </Text>
                        </VStack>
                        <VStack align="end" spacing={0}>
                          <Badge colorScheme={getScoreColor(board.avgScore)}>
                            {formatNumber(board.avgScore, 2)}
                          </Badge>
                          <Text fontSize="xs" color="gray.500">
                            {board.tasks} done
                          </Text>
                        </VStack>
                      </HStack>
                    ))}
                  </Stack>
                ) : (
                  <Text color="gray.500" fontSize="sm">No recent activity</Text>
                )}
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Project Statistics */}
          <Card shadow="lg">
            <CardHeader>
              <HStack>
                <Icon as={FaProjectDiagram} color="blue.500" />
                <Heading size="md">Top 5 Projects</Heading>
              </HStack>
            </CardHeader>
            <CardBody pt={0}>
              {statistics.projectStats.length > 0 ? (
                <TableContainer>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Rank</Th>
                        <Th>Project</Th>
                        <Th>Boards</Th>
                        <Th>Tasks</Th>
                        <Th>Total Score</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {statistics.projectStats.map((project, index) => (
                        <Tr key={project.projectId}>
                          <Td>
                            <Badge colorScheme={index < 3 ? 'yellow' : 'gray'}>
                              #{index + 1}
                            </Badge>
                          </Td>
                          <Td>
                            <Text fontWeight="bold" fontSize="sm">
                              {project.projectName}
                            </Text>
                          </Td>
                          <Td>
                            <Badge colorScheme="blue">{project.boards}</Badge>
                          </Td>
                          <Td>
                            <Badge colorScheme="green">{project.tasks}</Badge>
                          </Td>
                          <Td>
                            <Badge colorScheme="yellow" fontSize="md">
                              {formatNumber(project.totalScore, 0)} pts
                            </Badge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              ) : (
                <Text color="gray.500" fontSize="sm">No project data available</Text>
              )}
            </CardBody>
          </Card>

          {/* Quick Actions */}
          <Card shadow="md" bg="blue.50">
            <CardBody>
              <HStack spacing={4} justify="center" flexWrap="wrap">
                <Button
                  colorScheme="blue"
                  size="lg"
                  leftIcon={<FaTasks />}
                  onClick={() => navigate('/management/score/tasks')}
                >
                  View All Boards
                </Button>
                <Button
                  colorScheme="green"
                  size="lg"
                  leftIcon={<FaChartLine />}
                  variant="outline"
                  onClick={() => navigate('/management/score/tasks')}
                >
                  Detailed Analytics
                </Button>
              </HStack>
            </CardBody>
          </Card>
        </>
      )}

      {/* Date Filter Modal */}
      <Modal size="xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <Icon as={FaRegCalendar} color="blue.500" />
              <Text>{t('select')} {t('date')}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <DatePicker onDateChange={onDateChange} />
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button colorScheme="gray" onClick={onClose}>
                {t('close')}
              </Button>
              <Button
                colorScheme="blue"
                onClick={() => {
                  getDashboardData();
                  onClose();
                }}
              >
                Apply Filter
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
}

export default ManagementDashboard;